import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { createOrder, verifyPayment } from "../redux/actions/Actions";

const CARDS_PER_PAGE = 3;

const ProgramCard = ({ data }) => {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!Array.isArray(data) || data.length === 0) return null;

  const getPoints = (details) => {
    if (!details) return ["Comprehensive course content"];
    return details.split(/\n|\.\s+/).filter(p => p.trim().length > 10).slice(0, 4)
      .map(p => p.trim().replace(/\.$/, ''));
  };

  const handleNext = () => setCurrentIndex(i => Math.min(i + CARDS_PER_PAGE, data.length - CARDS_PER_PAGE));
  const handlePrev = () => setCurrentIndex(i => Math.max(i - CARDS_PER_PAGE, 0));

  const loadRazorpay = () => new Promise(resolve => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const handleEnroll = async (item) => {
    const email = JSON.parse(localStorage.getItem("user"))?.email;
    if (!email) { navigate("/registerform"); return; }
    const ok = await loadRazorpay();
    if (!ok) { alert("Razorpay failed to load."); return; }
    const { payload: order } = await dispatch(createOrder(item.cost, email));
    if (!order?.id) { alert("Failed to create order."); return; }
    new window.Razorpay({
      key: "rzp_test_3QwB9Z9JVzyeGE",
      currency: "INR",
      amount: item.cost * 100,
      name: item.program_name,
      order_id: order.id,
      handler: async (res) => {
        await dispatch(verifyPayment({ ...res, amount: item.cost, email }));
        alert("✅ Payment successful!");
      },
      prefill: { email },
      theme: { color: "#2563EB" },
    }).open();
  };

  const visible  = data.slice(currentIndex, currentIndex + CARDS_PER_PAGE);
  const canPrev  = currentIndex > 0;
  const canNext  = currentIndex < data.length - CARDS_PER_PAGE;

  // Color palette per card position
  const accents = ["#2563EB", "#0891B2", "#7C3AED", "#059669", "#D97706", "#DC2626"];

  return (
    <div style={s.section}>
      {/* Header */}
      <div style={s.header}>
        <p style={s.eyebrow}>PROGRAMS</p>
        <h2 style={s.heading}>Explore Our Programs</h2>
        <p style={s.sub}>Choose the program that matches your career goals and start building real skills today.</p>
      </div>

      {/* Cards + nav */}
      <div style={s.cardsWrap}>
        {/* Prev */}
        {canPrev && (
          <button style={s.navBtn} onClick={handlePrev} aria-label="Previous">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
        )}

        <div style={s.grid}>
          {visible.map((item, idx) => {
            const accent = accents[(currentIndex + idx) % accents.length];
            const points = getPoints(item.program_details);
            return (
              <div key={item.program_id} style={s.card}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(37,99,235,0.14)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
              >
                {/* Top accent bar */}
                <div style={{ height: 4, background: accent, borderRadius: '14px 14px 0 0' }} />

                <div style={s.cardBody}>
                  {/* Badge */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <span style={{ ...s.badge, background: accent + '18', color: accent, border: `1px solid ${accent}30` }}>
                      {item.duration || 'Flexible'}
                    </span>
                    <span style={{ fontSize: 22 }}>🎓</span>
                  </div>

                  {/* Title */}
                  <h3 style={s.cardTitle}>{item.program_name}</h3>

                  {/* Points */}
                  <ul style={s.pointList}>
                    {points.map((pt, i) => (
                      <li key={i} style={s.point}>
                        <span style={{ ...s.tick, color: accent }}>✓</span>
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Price */}
                  <div style={s.priceRow}>
                    <span style={{ ...s.price, color: accent }}>
                      {item.cost ? `₹${Number(item.cost).toLocaleString()}` : 'Contact for pricing'}
                    </span>
                    <Link to={`/programDetail/${item.program_id}`} style={s.detailsLink}>
                      View Details →
                    </Link>
                  </div>

                  {/* Enroll button */}
                  <button
                    style={{ ...s.enrollBtn, background: accent }}
                    onClick={() => handleEnroll(item)}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    Enroll Now
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Next */}
        {canNext && (
          <button style={s.navBtn} onClick={handleNext} aria-label="Next">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        )}
      </div>

      {/* Dots */}
      {data.length > CARDS_PER_PAGE && (
        <div style={s.dots}>
          {Array.from({ length: Math.ceil(data.length / CARDS_PER_PAGE) }).map((_, i) => (
            <button key={i} onClick={() => setCurrentIndex(i * CARDS_PER_PAGE)}
              style={{ ...s.dot, background: i === Math.floor(currentIndex / CARDS_PER_PAGE) ? '#2563EB' : '#CBD5E1' }} />
          ))}
        </div>
      )}

      {/* View all */}
      <div style={{ textAlign: 'center', marginTop: 32 }}>
        <Link to="/programs" style={s.viewAll}>View all programs →</Link>
      </div>
    </div>
  );
};

const s = {
  section: {
    background:    '#F8FAFC',
    padding:       '72px 5% 80px',
    fontFamily:    "'Inter','Segoe UI',sans-serif",
  },
  header:  { textAlign: 'center', marginBottom: 52 },
  eyebrow: { fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', color: '#2563EB', textTransform: 'uppercase', marginBottom: 10 },
  heading: { fontSize: 'clamp(28px,3.5vw,44px)', fontWeight: 800, color: '#0F172A', letterSpacing: '-0.03em', margin: '0 0 14px' },
  sub:     { fontSize: 16, color: '#64748B', lineHeight: 1.7, maxWidth: 520, margin: '0 auto' },

  cardsWrap: { display: 'flex', alignItems: 'center', gap: 16, maxWidth: 1200, margin: '0 auto' },
  grid: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 24,
  },

  card: {
    background:    '#FFFFFF',
    borderRadius:  '14px',
    border:        '1px solid #E2E8F0',
    boxShadow:     '0 2px 12px rgba(0,0,0,0.06)',
    overflow:      'hidden',
    transition:    'transform 0.25s ease, box-shadow 0.25s ease',
    display:       'flex',
    flexDirection: 'column',
  },
  cardBody:  { padding: '20px 24px 24px', flex: 1, display: 'flex', flexDirection: 'column' },
  badge: {
    fontSize:      11,
    fontWeight:    700,
    padding:       '4px 12px',
    borderRadius:  50,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  cardTitle: { fontSize: 18, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em', lineHeight: 1.3, margin: '0 0 16px' },

  pointList: { listStyle: 'none', padding: 0, margin: '0 0 20px', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 },
  point:     { display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#475569', lineHeight: 1.5 },
  tick:      { fontWeight: 700, fontSize: 14, flexShrink: 0, marginTop: 1 },

  priceRow:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  price:       { fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' },
  detailsLink: { fontSize: 13, fontWeight: 600, color: '#2563EB', textDecoration: 'none' },

  enrollBtn: {
    width:         '100%',
    padding:       '13px',
    border:        'none',
    borderRadius:  '10px',
    color:         '#fff',
    fontSize:      14,
    fontWeight:    700,
    cursor:        'pointer',
    letterSpacing: '0.03em',
    transition:    'opacity 0.2s',
  },

  navBtn: {
    flexShrink:    0,
    width:         44,
    height:        44,
    borderRadius:  '50%',
    border:        '1.5px solid #E2E8F0',
    background:    '#FFFFFF',
    color:         '#1E293B',
    display:       'flex',
    alignItems:    'center',
    justifyContent:'center',
    cursor:        'pointer',
    boxShadow:     '0 2px 8px rgba(0,0,0,0.06)',
  },

  dots: { display: 'flex', justifyContent: 'center', gap: 8, marginTop: 28 },
  dot:  { width: 8, height: 8, borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 0, transition: 'background 0.2s' },

  viewAll: {
    display:       'inline-block',
    fontSize:      15,
    fontWeight:    600,
    color:         '#2563EB',
    textDecoration:'none',
    padding:       '10px 28px',
    border:        '1.5px solid #BFDBFE',
    borderRadius:  50,
  },
};

export default ProgramCard;
